<template>
  <v-list-tile-content v-if="hasInitiative">
    <v-layout row wrap align-center class="full-width">
    <p class="d-flex flex-row align-center ma-0">
      <span v-if="view.hasLongName">
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <span v-on="on" :class="{'primary--text':isSelected}">
              <span class="font-weight-bold text-no-wrap">{{
                view.initiativeName | truncate(maxNameLength)
              }}</span>
              <span class="text-no-wrap">
                [ <i>{{ view.initiativeKey }}</i> ]
              </span>
            </span>
          </template>
          <span>{{ view.initiativeName }}</span>
        </v-tooltip>
      </span>
      <span v-else>
        <span :class="{'primary--text':isSelected}">
          <span class="font-weight-bold">{{ view.initiativeName }}</span> [
          <span class="text-caption">{{ view.initiativeKey }}</span> ]
        </span>
      </span>

      <span
        class="initiative_selector_initiative--status mx-1"
        :class="view.initiativeDisplayStatus || ''"
      >
        {{ view.initiativeDisplayStatus | whatsappTemplateStatus }}
      </span>

      <QualityChip
        v-if="view.showInitiativeQuality"
        :quality="view.initiativeMetaQualityScore"
      ></QualityChip>

      <v-tooltip bottom v-if="!!initiative.creditor_id">
        <template v-slot:activator="{ on }">
          <span class="ml-2" v-on="on"> ★ </span>
        </template>
        <span>{{
          `iniciativa e exclusiva do credor #${initiative.creditor_id}`
        }}</span>
      </v-tooltip>

      <v-chip small outline class="ma-0 mx-1">
        <span>{{
          view.initiativeMetaCategory | whatsappTemplateCategory
        }}</span>
      </v-chip>

      <ApprovalIcon
        :approved="view.initiativeApproved"
        :approvedBy="view.initiativeApprovedBy"
        :approvedAt="view.initiativeApprovedAt"
      ></ApprovalIcon>
    </p>

    <v-spacer></v-spacer>

    <OwnerChip
      v-if="view.hasSenderOwner && !hideBroker"
      :owner="view.senderOwner"
      disable-tooltip
    ></OwnerChip>
    </v-layout>

    <p class="d-flex align-center pl-2" :class="{'primary--text':isSelected}">
      <span v-if="view.hasSender" class="sender-name mr-1">
        <i># {{ view.senderId }}</i>
        {{ view.senderPhone | phoneNumber }}
      </span>

      <QualityChip
        v-if="view.showSenderQuality"
        :quality="view.senderQuality"
        tooltip="Qualidade do Sender"
        size="small"
      ></QualityChip>

      <FlaggedIcon v-if="view.showFlaggedSender"></FlaggedIcon>
    </p>
  </v-list-tile-content>
</template>

<script>
import ApprovalIcon from "@/components/ui/ApprovalIcon.vue";
import FlaggedIcon from "@/components/ui/FlaggedIcon.vue";
import QualityChip from "@/components/ui/QualityChip.vue";
import OwnerChip from "@/components/ui/OwnerChip.vue";

export default {
  name: "InitiativeDisplay",
  components: {
    ApprovalIcon,
    FlaggedIcon,
    QualityChip,
    OwnerChip,
  },
  props: {
    initiative: {
      type: Object,
      default: null,
    },
    maxNameLength: {
      type: Number,
      default: 30,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
    hideBroker: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    hasInitiative() {
      return !!this.initiative;
    },
    view() {
      const initiative = this.initiative || {};
      const sender = initiative.sender || null;
      const initiativeName = initiative.name || "";
      const initiativeMetaStatus = initiative.meta_status || "";
      const senderQuality = sender ? sender.meta_quality_rating_v2 || "" : "";

      return {
        initiativeName,
        initiativeKey: initiative.key || "-",
        hasLongName: initiativeName.length > this.maxNameLength,
        initiativeMetaStatus,
        initiativeDisplayStatus:
          initiative.active === false ? "DISABLED" : initiativeMetaStatus,
        initiativeMetaQualityScore: initiative.meta_quality_score,
        showInitiativeQuality:
          initiativeMetaStatus === "APPROVED" &&
          !!initiative.meta_quality_score,
        showCreditorStar: !!initiative.creditor_id,
        creditorId: initiative.creditor_id || "",
        creditorExclusiveTooltip: initiative.creditor_id
          ? `A iniciativa e exclusiva do credor #${initiative.creditor_id}`
          : "",
        initiativeMetaCategory: initiative.meta_category,
        initiativeApproved: initiative.approved,
        initiativeApprovedBy: initiative.approved_by,
        initiativeApprovedAt: initiative.approved_at,
        hasSender: !!sender,
        senderId: sender && sender.id ? sender.id : "",
        senderPhone: sender && sender.phone_number ? sender.phone_number : "",
        senderQuality,
        showSenderQuality: !!senderQuality,
        showFlaggedSender: !!(sender && sender.meta_status === "FLAGGED"),
        senderOwner: sender && sender.owner ? sender.owner : "",
        hasSenderOwner: !!(sender && sender.owner),
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.full-width {
  width: 100%;
}
.initiative_selector_initiative--status {
  border: 1px solid;
  border-radius: 2px;
  font-weight: 500;
  padding: 1px 2px;
  white-space: nowrap;
  font-size: 12px;
}

.initiative_selector_initiative--status.APPROVED {
  background-color: #ebf5f1;
  border-color: #b6d1c6;
  color: #247058;
}

.initiative_selector_initiative--status.APPROVED.LOW {
  background-color: #fff0f1;
  border-color: #facfd4;
  color: #ab1628;
}

.initiative_selector_initiative--status.APPROVED.MEDIUM {
  background-color: #fff8e3;
  border-color: #ffe299;
  color: #ad7410;
}

.initiative_selector_initiative--status.PENDING_DELETION,
.initiative_selector_initiative--status.REJECTED,
.initiative_selector_initiative--status.DISABLED {
  background-color: #fff0f1;
  border-color: #facfd4;
  color: #ab1628;
}

.initiative_selector_initiative--status.UNKNOWN {
  background-color: #e5e5e3;
  border-color: #a6a5a2;
  color: #545452;
}

/* Custom design for paused states */
.initiative_selector_initiative--status.PAUSED,
.initiative_selector_initiative--status.FIRST_PAUSED,
.initiative_selector_initiative--status.SECOND_PAUSED {
  background-color: #fff3e0;
  border-color: #ffcc80;
  color: #e65100;
}
</style>
